import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { getListingOverview } from '../../../../api/stays-reservations-client';
import type { StayFull, StayGap, StayOverview, StaysPolicy } from '../../../../interfaces/stays';
import { useResource } from '../../../hooks/use-resource';
import { ClockNote } from '../../../clock-note/clock-note';
import { StatusPill } from '../../../status-pill/status-pill';
import { FormSkeleton, formSkeletonHeight, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { Presence, Staleable, StateSwitch } from '../../../../motion';
import { Table, TableCaption, TableFrame, Tbody, Thead } from '../../../table/list-table';
import { formatDayKey } from '../../../../function-utils/building-time';
import { formatMoney } from '../../../../utils/stays-money';
import { touch, up } from '../../../../style-utils/media-queries';
import { layout, palette, radius, shadow, spacing, transition, typography } from '../../../../style-utils/theme';
import { Card, Empty, EmptyText } from '../../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { buildingClock } from '../stays-reservations-model';
import { ReadinessChecklist } from '../setup/readiness-checklist';
import { listingSectionHref } from '../listing/stays-listing-routes';
import { grossText, nightsLabel, shortName, sourceText } from '../reservations/reservation-labels';
import { ReservationHeadRow, ReservationRow } from '../reservations/reservation-row';
import { useAuth } from '../../../../context/auth-provider';
import { isCohost } from '../../../../auth/roles';

/**
 * THE LISTING OVERVIEW CARDS (plan S4, R-S4.27 / R-S4.28): who is in the unit
 * now, today's check-out and check-in, the next empty nights, this month's
 * money, and the next arrivals.
 *
 * Everything is on the BUILDING's clock, from the response's envelope: "today"
 * is building_today, the month is the building's month, the times are the
 * building's wall clock (data-time) and the dates are day keys printed as they
 * are. One ClockNote says so. The device's clock is never asked.
 */

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.tealTintSoft};
`;

const StripLabel = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.inkSoft};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.sm};
  min-width: 0;

  ${up('sm')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${up('xl')} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const CardHeading = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

const Strong = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
`;

const Line = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
`;

const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs} ${spacing.sm};
  min-width: 0;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const ViewAll = styled(Link)`
  display: inline-flex;
  align-items: center;
  /* The touch floor (J-S1.3 measured the bare text at 90×21). */
  min-height: ${layout.touchTarget};
  border-radius: ${radius.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.irisDeep};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_KEY = /^\d{4}-\d{2}$/;

/** "October" for '2026-10' (or a day key in it); the key itself otherwise. */
export const monthName = (key: string): string => {
  const day = MONTH_KEY.test(key) ? `${key}-01` : key;

  return DAY_KEY.test(day) ? formatDayKey(day, { month: 'long' }) : key;
};

const day = (key: string): string => formatDayKey(key);

const isOpenEnded = (g: StayOverview['next_gap']): g is { start: string; open_ended: true } =>
  !!g && (g as { open_ended?: boolean }).open_ended === true;

const withGross = (text: string, s: StayFull): string => {
  const gross = grossText(s);

  return gross ? `${text} · ${gross}` : text;
};

const OverviewCard: React.FC<{ title: string; testId: string; children: React.ReactNode }> = ({ title, testId, children }) => (
  <Card aria-label={title} data-testid={testId}>
    <CardHeading>{title}</CardHeading>
    <Lines>{children}</Lines>
  </Card>
);

export interface StaysOverviewCardsProps {
  listingId: string;
  policy?: StaysPolicy;
  /** Open one stay (the Stays tab's deep link with its edit modal). */
  onOpenStay: (id: string) => void;
  /** stays:manage — the "Next arrivals" rows' Edit, menu and paid toggle. */
  canManage?: boolean;
  /** Bump to read the overview again (a stay was added on the page). */
  reloadKey?: number;
}

export const StaysOverviewCards: React.FC<StaysOverviewCardsProps> = ({
  listingId,
  onOpenStay,
  canManage = false,
  reloadKey = 0,
}) => {
  const resource = useResource<StayOverview>(() => getListingOverview(listingId), {
    deps: [listingId, reloadKey],
    fallbackMessage: "Couldn't load this listing",
  });
  const o = resource.data;

  const view: 'loading' | 'error' | 'ready' = resource.isLoading ? 'loading' : !o ? 'error' : 'ready';

  // Independent Stays plan ISR: what the listing still misses, until it is ready.
  return (
    <>
      <ReadinessChecklist listingId={listingId} canFix={canManage} hideWhenReady />
      <StateSwitch state={view} minHeight={formSkeletonHeight(4)}>
        {view === 'loading' ? (
          <FormSkeleton fields={4} label="the overview" />
        ) : view === 'error' || !o ? (
          <LoadError message="Couldn't load this listing" onRetry={() => void resource.reload()} />
        ) : (
          <Staleable stale={resource.isRefreshing}>
            <OverviewBody o={o} listingId={listingId} canManage={canManage} onOpenStay={onOpenStay} onChanged={() => void resource.reload()} />
          </Staleable>
        )}
      </StateSwitch>
    </>
  );
};

const OverviewBody: React.FC<{
  o: StayOverview;
  listingId: string;
  canManage: boolean;
  onOpenStay: (id: string) => void;
  onChanged: () => void;
}> = ({ o, listingId, canManage, onOpenStay, onChanged }) => {
  const current = o.current_stay;
  const departing = o.departing_today && o.departing_today[0];
  const arriving = o.arriving_today && o.arriving_today[0];
  const gap = o.next_gap;
  const month = o.month;
  // IS5: a co-host's overview carries no amounts; the month card counts nights only.
  const hideMoney = isCohost(useAuth().user?.role);
  const currency = month.currency || o.listing?.currency || 'EUR';
  const buildingToday = o.building_today || o.today;
  const nextArrivals = (o.next_arrivals || []).slice(0, 10);

  return (
    <Stack data-testid="stays-overview">
      <ClockNote clock={buildingClock(o.building_zone || o.listing?.timezone)} />

      {current ? (
        <Strip data-testid="stays-current" data-stay-id={current.id}>
          <StripLabel>Current stay</StripLabel>
          <Strong>{shortName(current)}</Strong>
          <StatusPill tone="brand">{sourceText(current.source)}</StatusPill>
          <Line data-check-in={current.check_in} data-check-out={current.check_out}>
            {`${nightsLabel(current.nights)} · ${day(current.check_in)} → ${day(current.check_out)}`}
          </Line>
        </Strip>
      ) : (
        <Strip data-testid="stays-current">
          <StripLabel>Vacant now</StripLabel>
        </Strip>
      )}

      <Grid>
        <OverviewCard title="Departing today" testId="stays-departing">
          {departing ? (
            <>
              <Strong>{shortName(departing)}</Strong>
              {departing.departed ? (
                <Line>(Departed) · Stay completed</Line>
              ) : (
                <Line data-time={departing.effective.departure_time}>
                  {withGross(`Check-out ${departing.effective.departure_time}`, departing)}
                </Line>
              )}
            </>
          ) : (
            <Line>No check-out today</Line>
          )}
        </OverviewCard>

        <OverviewCard title="Arriving today" testId="stays-arriving">
          {arriving ? (
            <>
              <Strong>{shortName(arriving)}</Strong>
              <Line data-time={arriving.effective.arrival_time}>
                {withGross(`Check-in ${arriving.effective.arrival_time}`, arriving)}
              </Line>
              <Line data-check-out={arriving.check_out}>{`${nightsLabel(arriving.nights)} until ${day(arriving.check_out)}`}</Line>
            </>
          ) : (
            <Line>No check-in today</Line>
          )}
        </OverviewCard>

        <OverviewCard title="Next gap" testId="stays-gap">
          {isOpenEnded(gap) ? (
            <>
              <Strong>Open-ended</Strong>
              <Line data-start={gap.start}>{`From ${day(gap.start)}`}</Line>
            </>
          ) : gap && (gap as StayGap).nights > 0 ? (
            <>
              <Strong>{nightsLabel((gap as StayGap).nights)}</Strong>
              <Line data-start={gap.start} data-end={(gap as StayGap).end}>
                {`${day(gap.start)} → ${day((gap as StayGap).end)}`}
              </Line>
            </>
          ) : (
            <Line>No gap nights</Line>
          )}
        </OverviewCard>

        <OverviewCard title={monthName(month.key)} testId="stays-month">
          {hideMoney ? (
            <Strong>{nightsLabel(month.nights)}</Strong>
          ) : month.gross_cents == null && month.payout_cents == null ? (
            <>
              <Strong>No amounts recorded</Strong>
              <Line>{nightsLabel(month.nights)}</Line>
            </>
          ) : (
            <>
              <Strong>{month.gross_cents != null ? formatMoney(month.gross_cents, currency) : '—'}</Strong>
              <Line>
                {[month.payout_cents != null ? `Net ${formatMoney(month.payout_cents, currency)}` : null, nightsLabel(month.nights)]
                  .filter(Boolean)
                  .join(' · ')}
              </Line>
            </>
          )}
        </OverviewCard>
      </Grid>

      <Stack as="section" aria-labelledby={`stays-next-${listingId}`}>
        <SectionHead>
          <SectionTitle id={`stays-next-${listingId}`}>Next arrivals</SectionTitle>
          <ViewAll to={listingSectionHref(listingId, 'stays')}>View all stays</ViewAll>
        </SectionHead>
        {nextArrivals.length ? (
          <TableFrame>
            <Table data-stack="lg">
              <TableCaption>Next arrivals</TableCaption>
              <Thead>
                <ReservationHeadRow canManage={canManage} />
              </Thead>
              <Tbody>
                <Presence>
                  {nextArrivals.map((stay, index) => (
                    <ReservationRow
                      key={stay.id}
                      stay={stay}
                      index={index}
                      buildingNow={o.building_now}
                      buildingToday={buildingToday}
                      canManage={canManage}
                      nextArrival={index === 0}
                      onEdit={canManage ? s => onOpenStay(s.id) : undefined}
                      onChanged={onChanged}
                    />
                  ))}
                </Presence>
              </Tbody>
            </Table>
          </TableFrame>
        ) : (
          <Empty role="status">
            <EmptyText>No arrivals booked.</EmptyText>
          </Empty>
        )}
      </Stack>
    </Stack>
  );
};
